/**
 * Centralized API Service with caching and deduplication
 * 
 * Features:
 * - In-memory cache with TTL (time-to-live)
 * - Deduplicates concurrent requests (no duplicate in-flight calls)
 * - Automatic token injection
 * - CSRF token support for state-changing requests
 * - Consistent error handling
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api2b.pro';

// ── Cache configuration ──────────────────────────────────────────
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const SHORT_TTL = 30 * 1000;       // 30 seconds for frequently-changing data

const cache = new Map();
const pendingRequests = new Map();

// ── CSRF token management ────────────────────────────────────────
let csrfToken = null;

/**
 * Attempt to fetch a CSRF token from the server.
 * Call this once on app initialization.
 * The server should set a cookie and return the token.
 */
async function fetchCsrfToken() {
  try {
    const res = await fetch(`${API_URL}/sanctum/csrf-cookie`, {
      credentials: 'include',
    });
    if (res.ok) {
      // Try to extract token from response or from cookie
      const data = await res.json().catch(() => null);
      csrfToken = data?.token || null;
    }
  } catch {
    // CSRF is optional - don't block the app if it fails
    console.debug('CSRF token fetch skipped (endpoint may not exist)');
  }
}

/**
 * Get the current CSRF token, attempting to fetch if not available.
 */
function getCsrfToken() {
  return csrfToken;
}

/**
 * Initialize CSRF protection. Safe to call multiple times.
 */
function initCsrf() {
  if (csrfToken === null) {
    fetchCsrfToken();
  }
}

// ── Helper: build headers with auth & CSRF ──────────────────────
function buildHeaders(extraHeaders = {}, method = 'GET') {
  const headers = { ...extraHeaders };
  
  // Auth token
  const token = (typeof window !== 'undefined') ? localStorage.getItem('token') : null;
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  // CSRF token for state-changing requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers['X-XSRF-TOKEN'] = csrf;
      headers['X-CSRF-TOKEN'] = csrf;
    }
  }
  
  if (!headers['Content-Type'] && method !== 'GET' && extraHeaders.body) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
}

function getCacheKey(url, options = {}) {
  const body = options.body || '';
  const method = options.method || 'GET';
  return `${method}:${url}:${body}`;
}

function getFromCache(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > entry.ttl) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(key, data, ttl = DEFAULT_TTL) {
  cache.set(key, { data, timestamp: Date.now(), ttl });
}

/**
 * Core fetch wrapper with caching and deduplication
 */
async function cachedFetch(url, options = {}, ttl = DEFAULT_TTL) {
  const key = getCacheKey(url, options);
  
  // Return cached data if valid
  const cached = getFromCache(key);
  if (cached) return cached;

  // Deduplicate in-flight requests
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  // Build headers with auth + CSRF
  const headers = buildHeaders(options.headers, options.method || 'GET');

  const promise = fetch(url, { ...options, headers, credentials: 'include' })
    .then(async (res) => {
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const err = new Error(data?.message || `HTTP ${res.status}`);
        err.status = res.status;
        err.data = data;
        throw err;
      }
      // Cache successful GET responses only
      if (!options.method || options.method === 'GET') {
        setCache(key, data, ttl);
      }
      return data;
    })
    .finally(() => {
      pendingRequests.delete(key);
    });

  pendingRequests.set(key, promise);
  return promise;
}

/**
 * Public API methods with appropriate caching TTLs.
 * All methods use buildHeaders() internally for automatic auth + CSRF.
 */
const api = {
  // ── Initialization ───────────────────────────────────────────
  init() {
    initCsrf();
  },

  // ── Site config (long cache - rarely changes) ──
  getLogo(origin) {
    const params = origin ? `?origin=${encodeURIComponent(origin)}` : '';
    return cachedFetch(`${API_URL}/api/logo${params}`, {}, 10 * 60 * 1000);
  },

  getGeneral() {
    return cachedFetch(`${API_URL}/api/general`, {}, 5 * 60 * 1000);
  },

  getSliders() {
    return cachedFetch(`${API_URL}/api/sliders`, {}, 2 * 60 * 1000);
  },

  getSocialLinks() {
    return cachedFetch(`${API_URL}/api/sociallink`, {}, 10 * 60 * 1000);
  },

  getPaymentMethods() {
    return cachedFetch(`${API_URL}/api/payment-methods`, {}, 2 * 60 * 1000);
  },

  getPromotions() {
    return cachedFetch(`${API_URL}/api/promotion`, {}, 2 * 60 * 1000);
  },

  // ── User data (short cache) ──
  getUser(remoteId) {
    return cachedFetch(
      `${API_URL}/api/user/show?remote_id=${encodeURIComponent(remoteId)}`,
      {}, SHORT_TTL
    );
  },

  getTransactions(remoteId) {
    return cachedFetch(
      `${API_URL}/api/transaction/show?remote_id=${encodeURIComponent(remoteId)}`,
      {}, SHORT_TTL
    );
  },

  getTurnovers(remoteId) {
    return cachedFetch(
      `${API_URL}/api/turnovers?remote_id=${encodeURIComponent(remoteId)}`,
      {}, SHORT_TTL
    );
  },

  getBalance() {
    return cachedFetch(
      `${API_URL}/api/balance`,
      { method: 'POST' },
      10 * 1000
    );
  },

  getMarketBlock() {
    return cachedFetch(
      `${API_URL}/api/market/block`,
      {}, 30 * 1000
    );
  },

  // ── Auth (never cached) ──
  login(username, password) {
    const headers = buildHeaders({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    }, 'POST');
    return fetch(`${API_URL}/api/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ username, password }),
      credentials: 'include',
    }).then(res => res.json());
  },

  signUp(payload) {
    const headers = buildHeaders({
      'Content-Type': 'application/json',
      'Origin': window.location.origin,
    }, 'POST');
    return fetch(`${API_URL}/api/v1/create-players`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      credentials: 'include',
    }).then(res => res.json());
  },

  // ── Generic mutation helpers (never cached) ──
  post(url, body) {
    return fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: buildHeaders({}, 'POST'),
      body: JSON.stringify(body),
      credentials: 'include',
    }).then(async (res) => {
      const data = await res.json().catch(() => null);
      if (!res.ok) throw { status: res.status, data };
      return data;
    });
  },

  get(url, params = {}) {
    const query = new URLSearchParams(params).toString();
    const fullUrl = query ? `${API_URL}${url}?${query}` : `${API_URL}${url}`;
    return cachedFetch(fullUrl, {}, SHORT_TTL);
  },

  // ── Cache management ──
  invalidate(pattern) {
    for (const key of cache.keys()) {
      if (key.includes(pattern)) cache.delete(key);
    }
  },

  clearCache() {
    cache.clear();
  },
};

export default api;
export { API_URL, initCsrf };
